import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2, Leaf } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatPrice, readApiError } from "@/lib/format";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError("No session_id in URL");
      setLoading(false);
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/checkout/status`, {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        setStatus(res.data);
        if (
          res.data.payment_status === "paid" ||
          res.data.status === "complete" ||
          attempts >= 6
        ) {
          setLoading(false);
        } else {
          setTimeout(poll, 1500);
        }
      } catch (e) {
        if (!cancelled) {
          setError(readApiError(e));
          setLoading(false);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const isMembership = status?.mode === "subscription";
  const productName =
    status?.metadata?.product_name ||
    status?.line_items?.[0]?.description ||
    (isMembership ? "Coastal Roots Community Membership" : "Order");
  const amount = status?.amount_total;
  const currency = status?.currency || "usd";

  return (
    <PublicLayout>
      <section
        data-testid="checkout-success"
        className="mx-auto max-w-2xl px-6 py-24 text-center"
      >
        {loading ? (
          <div className="flex flex-col items-center gap-4 py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">
              Confirming with Stripe…
            </p>
          </div>
        ) : error ? (
          <div className="space-y-4 py-20">
            <h1 className="font-serif text-3xl">We couldn't confirm your order.</h1>
            <p
              data-testid="checkout-success-error"
              className="text-sm text-muted-foreground"
            >
              {error}
            </p>
            <Link to="/">
              <Button variant="outline">Back to the shop</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
              {status?.payment_status === "paid" ? (
                <CheckCircle2 className="h-8 w-8" />
              ) : (
                <Leaf className="h-8 w-8" />
              )}
            </span>
            <h1
              data-testid="checkout-success-headline"
              className="font-serif text-4xl sm:text-5xl"
            >
              {status?.payment_status === "paid"
                ? "Thank you. You're in."
                : "Almost there."}
            </h1>
            <p
              className="text-base text-muted-foreground"
              data-testid="checkout-success-detail"
            >
              {status?.payment_status === "paid"
                ? isMembership
                  ? "Your $50/mo Coastal Roots Community membership is live. Look out for the welcome email with your Skool invite link."
                  : `Your order is in. We'll wrap it up at the bench and send a tracking number within a week.`
                : `Stripe is finishing up the charge. Hang tight, or refresh in a minute.`}
              {/* apostrophe-escaped above where needed */}
            </p>

            <div className="mx-auto max-w-md space-y-2 rounded-xl border border-border/60 bg-secondary/30 p-5 text-left text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Item</span>
                <span className="font-medium" data-testid="success-item">
                  {productName}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium" data-testid="success-total">
                  {amount != null ? formatPrice(amount, currency) : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium" data-testid="success-status">
                  {status?.payment_status || "pending"}
                </span>
              </div>
              {status?.customer_email && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{status.customer_email}</span>
                </div>
              )}
              {sessionId && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Session</span>
                  <span className="truncate font-mono text-xs">{sessionId}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Link to="/shop">
                <Button variant="outline">Keep browsing</Button>
              </Link>
              <Link to="/">
                <Button className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]">
                  Back home
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
